import React, { useEffect, useState, createContext, useContext } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
interface UserContextType {
  session: Session | null;
  supabaseUser: User | null;
  user: {
    email: string;
    linkedPhone: string | null;
    plan: {
      isPro: boolean;
    };
    usage: {
      minutesUsed: number;
      percentage: number;
    };
  } | null;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{
    error: Error | null;
  }>;
  signUp: (email: string, password: string) => Promise<{
    error: Error | null;
  }>;
  resetPassword: (email: string) => Promise<{
    error: Error | null;
  }>;
  refreshUser: () => Promise<void>;
}
const UserContext = createContext<UserContextType | undefined>(undefined);
export function UserProvider({
  children
}: {
  children: ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [linkedPhone, setLinkedPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setSession(session);
      setSupabaseUser(session?.user ?? null);
      // Setup user in sms_users table if they just signed up
      if (session?.user?.email) {
        setupUserInDatabase(session.user.email, session.user.id);
        checkLinkedPhone(session.user.email);
      }
      setLoading(false);
    });
    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setSupabaseUser(session?.user ?? null);
      // Setup user in sms_users table on sign in/sign up
      if (session?.user?.email) {
        setupUserInDatabase(session.user.email, session.user.id);
        checkLinkedPhone(session.user.email);
      } else {
        setLinkedPhone(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  // Setup user in sms_users table
  const setupUserInDatabase = async (email: string, userId: string) => {
    try {
      // Check if user exists in sms_users
      const {
        data: existingUser,
        error: checkError
      } = await supabase.from('sms_users').select('email, credits_remaining, auth_user_id').eq('email', email).single();
      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking sms_users:', checkError);
        return;
      }
      if (!existingUser) {
        // User doesn't exist - create without specifying credits_remaining
        // so database default of 5 credits applies
        const {
          error: insertError
        } = await supabase.from('sms_users').insert({
          email: email,
          auth_user_id: userId
        });
        if (insertError) {
          console.error('Error creating sms_users entry:', insertError);
        } else {
          console.log('✅ Created sms_users entry for:', email, 'with 5 free credits');
        }
      } else {
        // Link auth_user_id if missing
        if (!existingUser.auth_user_id) {
          await supabase.from('sms_users').update({
            auth_user_id: userId
          }).eq('email', email);
          console.log('✅ Linked auth_user_id for:', email);
        }
        console.log('✅ User exists:', email, 'Credits:', existingUser.credits_remaining);
      }
    } catch (error) {
      console.error('Error in user setup:', error);
    }
  };
  const checkLinkedPhone = async (email: string) => {
    try {
      const {
        data,
        error
      } = await supabase.from('sms_users').select('phone_number').eq('email', email).single();
      if (data?.phone_number) {
        setLinkedPhone(data.phone_number);
      }
    } catch (error) {
      console.error('Error checking linked phone:', error);
    }
  };
  const refreshUser = async () => {
    if (session?.user?.email) {
      await checkLinkedPhone(session.user.email);
    }
  };
  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setSupabaseUser(null);
  };
  const signIn = async (email: string, password: string) => {
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) return {
        error
      };
      return {
        error: null
      };
    } catch (err) {
      return {
        error: err as Error
      };
    }
  };
  const signUp = async (email: string, password: string) => {
    try {
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email,
        password
      });
      if (error) return {
        error
      };
      return {
        error: null
      };
    } catch (err) {
      return {
        error: err as Error
      };
    }
  };
  const resetPassword = async (email: string) => {
    try {
      const {
        error
      } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) return {
        error
      };
      return {
        error: null
      };
    } catch (err) {
      return {
        error: err as Error
      };
    }
  };
  // Mock user data for now - you can enhance this later
  const user = session ? {
    email: supabaseUser?.email || '',
    linkedPhone,
    plan: {
      isPro: false // You can check this from database later
    },
    usage: {
      minutesUsed: 0,
      percentage: 0
    }
  } : null;
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen" data-id="element-1912">
        <div className="text-center" data-id="element-1913">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" data-id="element-1914"></div>
          <p className="text-muted-foreground" data-id="element-1915">Loading...</p>
        </div>
      </div>;
  }
  return <UserContext.Provider value={{
    session,
    supabaseUser,
    user,
    signOut,
    signIn,
    signUp,
    resetPassword,
    refreshUser
  }} data-id="element-1916">
      {children}
    </UserContext.Provider>;
}
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}